<template>
    <div id="rating-distribution">
        <div class="content">
            <div class="chart-container">
                <div class="header">
                    <FilterTool v-model="filters" :isGraphShowing="true" :keep-state-on-query-params="true" />
                </div>
                <div
                    v-if="loading"
                    class="h-50 d-flex flex-column justify-content-center align-items-center text-align-center"
                >
                    <a-spin />
                    <p>{{ $t("message.loading") }}</p>
                </div>
                <div class="charts-wrapper">
                    <div class="sub-header">
                        <div class="left">
                            <md-icon>help</md-icon>
                            <div class="title">
                                {{ $t(`chatbotv2.ratingDistribution`) }}
                            </div>
                        </div>
                        <div class="right">
                            <div class="export-button" @click="exportToCsv">
                                <md-icon>system_update_alt</md-icon>
                                <span>{{ $t("chatbotv2.exportToCsv") }}</span>
                            </div>
                            <div class="export-button" @click="exportToExcel">
                                <md-icon>system_update_alt</md-icon>
                                <span>{{ $t("chatbotv2.exportToExcel") }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="!isEmpty()" class="charts-container">
                        <div class="statistic-box">
                            <span class="title">{{ $t("report.totalRatingCount") }}</span>
                            <span class="value">{{ getTotalRateCount() }}</span>
                        </div>
                        <div class="statistic-box">
                            <span class="title">{{ $t("report.averageRating") }}</span>
                            <span class="value">{{ getAvgRate() }}</span>
                        </div>
                        <div class="custom-card-report">
                            <h5>{{ $t("chatbotv2.ratingDistribution") }}</h5>
                            <PieChart :data="getOptions()" />
                        </div>
                    </div>
                </div>
                <div class="d-flex justify-content-center mt-5" v-if="isEmpty()">
                    <ChartNotFound :title="$t('message.dataNotFound')" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import SideMenuBar from "../../SideMenuBar";
import PieChart from "../../components/PieChart";
import FilterTool from "../archive/FilterTool";
import ChartNotFound from "../../components/ChartNotFound";
import { IS_CHARTS_PUBLIC } from "../../chatbot";
import { mapMutations, mapState, mapActions, mapGetters } from "vuex";
import ComingSoonWrapper from "./ComingSoonWrapper";
import { log2 } from "../../../../../utils/log2";
import _ from "lodash";
import moment from "moment";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import LineGraphic from "../../components/LineGraphic.vue";
import { expertToExcel, exportToCSV } from "../../../../../utils/utils";
import XLSX from "xlsx";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import ChatbotMenu from "../../ChatbotMenu.vue";

const log = log2("RatingDistribution");

export default {
    name: "RatingDistribution",
    components: {
        ChatbotMenu,
        LineGraphic,
        ChartNotFound,
        FilterTool,
        PieChart,
        SideMenuBar,
        ComingSoonWrapper,
    },
    data() {
        return {
            IS_CHARTS_PUBLIC,
            renderKey: 0,
            graphData: [],
            totalData: { message: this.$t("chatbotv2.totalData"), value: 0 },
            avgData: { message: this.$t("chatbotv2.avgData"), value: 0 },
            story: null,
            datePeriod: 30,
            loading: true,
            filters: {
                period: this.$route.query.period || "today",
                integration: this.$route.query.integration || null,
                storyId: this.$route.query.storyId || null,
                startDate: this.$route.query.startDate || moment().startOf("day").toISOString(),
                endDate: this.$route.query.endDate || moment().endOf("day").toISOString(),
            },
        };
    },
    methods: {
        ...mapActions({
            listStories: "CB/listStories",
        }),

        fetchData() {
            this.loading = true;
            if (this.filters.period !== "custom") {
                this.filters.startDate = null;
                this.filters.endDate = null;
                this.$router.replace({
                    query: {
                        period: this.filters.period,
                        integration: this.filters.integration,
                        storyId: this.filters.storyId,
                    },
                });
            }
            CHATBOT_API.REPORT.ratingDistribution({ ...this.filters })
                .then(async (res) => {
                    this.graphData = res.data.result;
                    this.renderKey += 1;
                    log("this.graph", res.data);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getOptions() {
            const { avgRating, totalRates } = _.get(this.graphData, "total", { avgRating: 0, totalRates: 0 });
            const points = _.get(this.graphData, "pointBasedCounts", { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
            const data = [
                { label: "⭐", legend: "⭐", value: points[1], color: "#4E79A7" },
                { label: "⭐⭐", legend: "⭐⭐", value: points[2], color: "#F28E2B" },
                { label: "⭐⭐⭐", legend: "⭐⭐⭐", value: points[3], color: "#E15759" },
                { label: "⭐⭐⭐⭐", legend: "⭐⭐⭐⭐", value: points[4], color: "#76B7B2" },
                { label: "⭐⭐⭐⭐⭐", legend: "⭐⭐⭐⭐⭐", value: points[5], color: "#59A14F" },
            ];
            return data;
        },
        isEmpty() {
            const { avgRating, totalRates } = _.get(this.graphData, "total", { avgRating: 0, totalRates: 0 });
            return totalRates === 0;
        },
        getTotalRateCount() {
            const { avgRating, totalRates } = _.get(this.graphData, "total", { avgRating: 0, totalRates: 0 });
            return totalRates;
        },
        getAvgRate() {
            const { avgRating, totalRates } = _.get(this.graphData, "total", { avgRating: 0, totalRates: 0 });
            return avgRating.toFixed(2);
        },
        exportToCsv() {
            const points = _.get(this.graphData, "pointBasedCounts", { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
            const csv = [
                { rating: "⭐", count: points[1] },
                { rating: "⭐⭐", count: points[2] },
                { rating: "⭐⭐⭐", count: points[3] },
                { rating: "⭐⭐⭐⭐", count: points[4] },
                { rating: "⭐⭐⭐⭐⭐", count: points[5] },
            ];
            exportToCSV(csv, `chatbot_rating_distributions.csv`);
        },
        exportToExcel() {
            const points = _.get(this.graphData, "pointBasedCounts", { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

            const data = [
                ["rating", "count"],
                ["⭐", points[1]],
                ["⭐⭐", points[2]],
                ["⭐⭐⭐", points[3]],
                ["⭐⭐⭐⭐", points[4]],
                ["⭐⭐⭐⭐⭐", points[5]],
            ];

            expertToExcel(data, `chatbot_rating_distributions.xlsx`, "rating_distributions");
        },
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    mounted() {
        this.listStories();
        this.fetchData();
    },
    beforeMount() {
        if (this.checkPermission(PERMISSION_TYPES["chatbot.reports.view"].tr.name) === false) {
            return router.push("/denied");
        }
    },
    watch: {
        filters: {
            handler(newVal) {
                if (newVal !== undefined) {
                    log("filters changed", this.filters);
                    const date = new Date();

                    if (!newVal.period) {
                        newVal.period = "today";
                    }

                    if (newVal.period === "today" || newVal.period === "yesterday") {
                        this.timeSeriesType = "hourly";
                        if (newVal.period === "today") this.datePeriod = moment().hour() + 1;
                        if (newVal.period === "yesterday") this.datePeriod = 24;
                    } else if (
                        newVal.period === "last-3-month" ||
                        newVal.period === "this-year" ||
                        newVal.period === "last-year"
                    ) {
                        this.timeSeriesType = "monthly";
                        if (newVal.period === "last-3-month") this.datePeriod = 3;
                        if (newVal.period === "this-year") this.datePeriod = date.getMonth() + 1;
                        if (newVal.period === "last-year") this.datePeriod = 12;
                    } else {
                        this.timeSeriesType = "daily";
                        if (newVal.period === "last7") this.datePeriod = 7;
                        else if (newVal.period === "last30") this.datePeriod = 30;
                        else if (newVal.period === "this-month") this.datePeriod = date.getDate();
                        else if (newVal.period === "last-month")
                            this.datePeriod = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
                    }
                    this.fetchData();
                }
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss">
#rating-distribution {
    width: 100%;
    height: auto;
    min-height: 100vh;
    display: flex;

    .side-menubar-wrapper {
        min-height: 100vh;
        flex: 0 0 15%;
        height: 100%;
    }

    .content {
        padding: 10px;
        display: flex;
        flex-direction: column;
        position: relative;
        background: white;
        width: 100%;
        height: 100%;
        min-height: 100vh;

        .chart-container {
            width: 100%;

            .header {
                width: 100%;
            }

            .chart {
                max-width: 1050px;
            }
        }
    }

    .sub-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .left {
            display: flex;
            justify-content: space-between;
            align-items: center;

            i {
                font-size: 18px !important;
            }

            .title {
                font-size: 19px;
            }
        }

        .right {
            min-width: 80px;
            display: flex;
            gap: 10px;
            padding: 5px 10px;
            user-select: none;

            .export-button {
                border: 1px solid #008aff;
                padding: 5px;
                cursor: pointer;
                color: #008aff;
                border-radius: 4px;

                &:hover {
                    background: var(--color-bg-hover);
                }

                i {
                    color: #008aff;
                    font-size: 17px !important;
                }
            }
        }
    }

    .charts-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 20px;
    }

    .custom-card-report {
        min-height: 500px !important;
        background-color: #ffffff;
        padding: 15px 20px;
        border-radius: 6px;
        border: 1px solid #e8e8e8;
    }

    .statistic-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 10px;
        border-radius: 6px;
        border: 1px solid #e8e8e8;
        background-color: #ffffff;

        .title {
            font-size: 14px;
            font-weight: 500;
            color: #000000;
        }

        .value {
            font-size: 20px;
            font-weight: 500;
            color: #000000;
        }
    }

    .charts-container {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
}
</style>
